import { DataSchema, schemaIsScalar } from '@integration-app/sdk'

import { useEventStopPropagation } from 'hooks/useEventStopPropagation'

import ObjectProperties from './ObjectProperties'
import { useSchemaBuilderContext } from './SchemaContext'
import { SchemaBuilderReferenceCollection, SchemaProps } from './types'
import { FieldActionBar } from '../components/Field/ActionBar'
import { Field } from '../components/Field/Field'
import { FieldPropertiesList } from '../components/Field/PropertiesList'
import { AddLevelButton } from '../components/NestedLevel/AddLevelButton'
import { NestedLevel } from '../components/NestedLevel/NestedLevel'
import { Cell, Row, Title } from '../components/Row'

export default function Schema({
  name,
  schema,
  isRequired = false,
  level,
  onChange,
  onNameChange,
  onRequiredChange,
  onDelete,
  onAdd,
  deprecatedNames,
}: SchemaProps) {
  const { referenceCollections, readOnly, isObject } = useSchemaBuilderContext()

  const { type, items, properties, $ref, ...schemaProperties } = schema ?? {}

  function handlePropertiesChange(value) {
    onChange({
      type,
      items,
      properties,
      $ref,
      ...(value ?? {}),
    })
  }

  function handleEscKey(value) {
    if (!value) {
      onDelete?.()
    }
  }

  function handleEnterKey() {
    onAdd?.()
  }

  const propertiesSchema = getPropertiesSchema(schema, {
    referenceCollections,
  })

  function handlePropertyAdd() {
    onChange({
      ...structuredClone(schema),
      properties: {
        ...structuredClone(schema.properties),
        ['']: {
          type: 'string',
        },
      },
    })
  }

  function patchSchema(patch) {
    onChange({
      ...(schema ?? {}),
      ...patch,
    })
  }

  return (
    <>
      {/* level '0' is starting point for the schema. We don't need to show initial object */}
      {(level > 0 || schema?.type !== 'object' || !isObject) && (
        <NestedLevel level={level}>
          <Row>
            <Cell.Name grow>
              <Title>
                <Field
                  schema={schema}
                  name={name}
                  onChange={onChange}
                  onEnter={handleEnterKey}
                  onEsc={handleEscKey}
                  onNameChange={onNameChange}
                  deprecatedNames={deprecatedNames}
                  level={level}
                />
              </Title>
            </Cell.Name>
            {!readOnly && (
              <FieldActionBar
                onDelete={onDelete}
                name={name}
                schema={schema}
                properties={schemaProperties}
                propertiesSchema={propertiesSchema}
                isRequired={isRequired}
                onPropertiesChange={handlePropertiesChange}
                onRequiredChange={onRequiredChange}
              />
            )}
            <FieldPropertiesList
              propertiesSchema={propertiesSchema}
              properties={schemaProperties}
              isRequired={isRequired}
            />
          </Row>
        </NestedLevel>
      )}
      {schema?.type === 'object' && (
        <>
          <ObjectProperties
            schema={schema}
            onAdd={handlePropertyAdd}
            level={level + 1}
            onChange={onChange}
          />
          {!readOnly && (
            <AddLevelButton
              field={{ level, name }}
              /* FIXME: strictNullCheck temporary fix */
              // @ts-expect-error TS(2322): Type '((event: EventType) => void) | undefined' is... Remove this comment to see the full error message
              onAdd={useEventStopPropagation(handlePropertyAdd)}
            />
          )}
        </>
      )}
      {schema?.type === 'array' && (
        <>
          <Schema
            name='item'
            schema={schema.items ?? {}}
            level={level + 1}
            onChange={(items) => patchSchema({ items })}
          />
        </>
      )}
    </>
  )
}

function getPropertiesSchema(
  schema: DataSchema,
  {
    referenceCollections,
  }: {
    referenceCollections?: SchemaBuilderReferenceCollection[]
  },
): DataSchema {
  const propertiesSchema: DataSchema = {
    type: 'object',
    properties: {
      title: {
        type: 'string',
      },
      description: {
        type: 'string',
      },
      default: {
        ...schema,
        title: 'Default',
      },
    },
  }

  if (schemaIsScalar(schema)) {
    /* FIXME: strictNullCheck temporary fix */
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    propertiesSchema.properties.enum = {
      type: 'array',
      items: {
        type: schema.type,
      },
    }
    /* FIXME: strictNullCheck temporary fix */
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    propertiesSchema.properties.referenceRecords = {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          name: { type: 'string' },
        },
      },
    }
    if (schema?.enum || schema?.referenceRecords) {
      /* FIXME: strictNullCheck temporary fix */
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      propertiesSchema.properties.allowCustom = {
        type: 'boolean',
      }
    }
  }

  if (schema?.type === 'string') {
    /* FIXME: strictNullCheck temporary fix */
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    propertiesSchema.properties.format = {
      type: 'string',
      enum: [
        'date-time',
        'date',
        'time',
        'email',
        'hostname',
        'ipv4',
        'ipv6',
        'uri',
      ],
    }
    /* FIXME: strictNullCheck temporary fix */
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    propertiesSchema.properties.minLength = {
      type: 'number',
    }
    /* FIXME: strictNullCheck temporary fix */
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    propertiesSchema.properties.maxLength = {
      type: 'number',
    }
  }

  if (schema?.type === 'number') {
    /* FIXME: strictNullCheck temporary fix */
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    propertiesSchema.properties.minimum = {
      type: 'number',
    }
    /* FIXME: strictNullCheck temporary fix */
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    propertiesSchema.properties.maximum = {
      type: 'number',
    }
  }

  if (schemaIsScalar(schema) && referenceCollections?.length) {
    /* FIXME: strictNullCheck temporary fix */
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    propertiesSchema.properties.referenceCollection = {
      type: 'object',
      properties: {
        key: {
          type: 'string',
          enum: referenceCollections.map((collection) => collection.key),
          allowCustom: true,
        },
        parameters: {
          type: 'object',
          additionalProperties: true,
        },
      },
      required: ['key'],
    }
  }

  /* FIXME: strictNullCheck temporary fix */
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  propertiesSchema.properties.readOnly = {
    type: 'boolean',
  }
  /* FIXME: strictNullCheck temporary fix */
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  propertiesSchema.properties.writeOnly = {
    type: 'boolean',
  }

  if (schema?.type === 'object') {
    /* FIXME: strictNullCheck temporary fix */
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    propertiesSchema.properties.additionalProperties = {
      type: 'boolean',
    }
  }

  return propertiesSchema
}
